import { ImageLoaderProps } from 'next/image'
import {
  ARTICLE_IMAGE_BASE,
  CLOUDFLARE_IMAGE_PREFIX,
  STATIC_IMAGE_BASE,
} from './constants'

/**
 * Additional Cloudflare image transform options beyond what Next.js provides.
 *
 * Next.js `<Image>` only passes `{ src, width, quality }` (ImageLoaderProps)
 * to the loader at render time, so these extra props can't be set via the
 * `<Image>` component directly. To use them, call `cloudflareLoader` yourself
 * — either in a wrapper passed to `<Image loader={...}>` or directly for
 * manual URL construction (e.g. preloading):
 *
 *   // as an <Image> loader with extra options
 *   <Image loader={(props) => cloudflareLoader({ ...props, blur: 175 })} />
 *
 *   // direct call for preloading
 *   const url = cloudflareLoader({ src, width: 76, quality: 25, blur: 175 })
 *
 * Full list of available transforms:
 * https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */
type ExtraCloudflareImageProps = {
  /** Output image format. Defaults to 'webp'. */
  format?: string
  /** Blur radius between 1 (slight blur) and 250 (maximum). */
  blur?: number
  /** https://developers.cloudflare.com/images/transform-images/transform-via-url/#trim */
  trim?: string
}

export const cloudflareLoader = ({
  src,
  width,
  quality = 75,
  format = 'webp',
  blur,
  trim,
}: ImageLoaderProps & ExtraCloudflareImageProps) => {
  if (src.includes('ctfassets.net')) {
    const regex = /w=([^&]*)/
    const match = regex.exec(src)

    // if the w parameter is found, replace its value with the desired width
    if (match !== null) {
      const currentValue = match[1]
      // Already optimized image src url coming from BE, change manually the width
      return src.replace(`w=${currentValue}`, `w=${width}`)
    }

    return `${src}?w=${width}`
  }

  const params = []
  params.push([`width=${width}`])
  params.push(`quality=${quality}`)
  params.push(`format=${format}`)
  if (blur) params.push(`blur=${blur}`)
  if (trim) params.push(`trim=${trim}`)

  if (src.includes(STATIC_IMAGE_BASE) || src.includes(ARTICLE_IMAGE_BASE)) {
    return `${CLOUDFLARE_IMAGE_PREFIX}/${params.join(',')}/${src}`
  }

  return `${src}${src.includes('?') ? '&' : '?'}${params.join('&')}`
}
